<template>
  <div class="page-container">
    <!-- 头部 -->
    <Header />

    <!-- 主要内容 -->
    <div class="news-page">
      <section class="about-content">
        <div class="container">
          <div class="about-header" ref="headerSection" :class="{ 'fade-in': isHeaderVisible }">
            <div class="title-box">
              <h1>新闻动态</h1>
              <p>了解美音最新动态，分享企业发展历程</p>
            </div>
            <div class="title-border"></div>
          </div>

          <div class="filter-bar" :class="{ 'fade-in': isHeaderVisible }">
            <div class="sort-options">
              <div class="sort-item" 
                v-for="(item, index) in sortOptions" 
                :key="index"
                :class="{ active: currentSort === item.value }"
                @click="handleSort(item.value)"
              >
                <el-icon><component :is="item.icon" /></el-icon>
                {{ item.label }}
              </div>
            </div>
            <div class="search-box">
              <el-input
                v-model="searchKeyword"
                placeholder="输入搜索关键词"
                @input="handleSearch"
                clearable
              >
                <template #prefix>
                  <el-icon class="search-icon"><Search /></el-icon>
                </template>
              </el-input>
            </div>
          </div>

          <div class="news-grid" :class="{ 'fade-in': isHeaderVisible }">
            <div class="news-item" 
              v-for="(item, index) in displayedNews" 
              :key="item.id"
              :class="{ 'animate': isHeaderVisible }"
              :style="{ 'animation-delay': `${index * 0.1}s` }"
              @click="goToDetail(item.id)"
            >
              <div class="news-image">
                <img :src="baseUrl + '/media' + item.cover" :alt="item.title">
                <div class="image-overlay">
                  <el-icon><View /></el-icon>
                </div>
              </div>
              <div class="news-content">
                <h3>{{ item.title }}</h3>
                <p class="description">{{ stripHtml(item.content) }}</p>
                <div class="news-footer">
                  <div class="meta-info">
                    <span class="date">
                      <el-icon><Calendar /></el-icon>
                      {{ formatDate(item.publish_time) }}
                    </span>
                    <span class="author" v-if="item.author">
                      <el-icon><User /></el-icon>
                      {{ item.author }}
                    </span>
                  </div>
                  <div class="views">
                    <el-icon><View /></el-icon>
                    {{ item.views }} 阅读
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="pagination" :class="{ 'fade-in': isHeaderVisible }">
            <el-pagination
              v-model:current-page="currentPage"
              :page-size="pageSize"
              :total="total"
              :pager-count="7"
              layout="prev, pager, next"
              @current-change="handlePageChange"
              background
            />
          </div>

          <div v-if="filteredNews.length === 0" class="empty-state">
            <el-empty description="暂无相关新闻" />
          </div>
        </div>
      </section>
    </div>

    <!-- 底部 -->
    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { Search, Calendar, User, Share, Star, View, Timer, Reading } from '@element-plus/icons-vue'
import Header from '@/layout/header/index.vue'
import Footer from '@/layout/footer/index.vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { getBaseApiUrl } from '@/utils/api-config'

const router = useRouter()
const baseUrl = getBaseApiUrl()
const headerSection = ref(null)
const isHeaderVisible = ref(false)
const hasAnimated = ref(false)
const searchKeyword = ref('')
const currentPage = ref(1)
const pageSize = ref(6)
const total = ref(0)
const newsList = ref([])

const sortOptions = [
  { label: '按时间排序', value: 'time', icon: 'Timer' },
  { label: '按浏览量排序', value: 'views', icon: 'Reading' }
]

const currentSort = ref('time')

const filteredNews = computed(() => {
  return newsList.value.filter(item => 
    item.title.toLowerCase().includes(searchKeyword.value.toLowerCase()) ||
    item.content.toLowerCase().includes(searchKeyword.value.toLowerCase())
  )
})

const displayedNews = computed(() => {
  let result = [...filteredNews.value]
  if (currentSort.value === 'views') {
    result.sort((a, b) => b.views - a.views)
  } else {
    result.sort((a, b) => new Date(b.publish_time) - new Date(a.publish_time))
  }
  
  const start = (currentPage.value - 1) * pageSize.value
  const end = start + pageSize.value
  return result.slice(start, end)
})

const handleSort = (value) => {
  currentSort.value = value
}

const handleSearch = () => {
  currentPage.value = 1
}

const handlePageChange = (page) => {
  currentPage.value = page
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const getNewsList = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/article/article/`, {
      params: {
        page: 1,
        limit: 10,
        column: 7,
        status: 'published'
      }
    })
    if (response.data.code === 2000) {
      newsList.value = response.data.data.data
    }
  } catch (error) {
    console.error('获取新闻列表失败:', error)
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

const stripHtml = (html) => {
  if (!html) return ''
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent || div.innerText || ''
}

const goToDetail = (id) => {
  router.push({
    name: 'NewsDetail',
    params: { id }
  })
}

// 定义 observer
const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.target === headerSection.value) {
      if (entry.isIntersecting && !hasAnimated.value) {
        isHeaderVisible.value = true
        hasAnimated.value = true
        observer.unobserve(headerSection.value)
      }
    }
  })
}, {
  threshold: 0.2
})

// 确保在 newsList 初始化后再设置 total
nextTick(() => {
  total.value = newsList.value.length
})

onMounted(() => {
  // 使用 nextTick 确保 DOM 更新后再进行观察
  nextTick(() => {
    if (headerSection.value) {
      observer.observe(headerSection.value)
    }
  })

  // 添加页面跳转后的滚动处理
  nextTick(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    })
  })

  getNewsList()
})
</script>

<style lang="scss" scoped>
.page-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  .news-page {
    flex: 1;
    padding-top: 96px; // header 的高度
    margin-top: 0; // 重要：确保没有额外的 margin
  }

  .about-content {
    padding: 60px 0;
    background: #fff;
    min-height: calc(100vh - 96px - 200px); // 减去 header 和 footer 的高度
    position: relative; // 添加相对定位
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;

    .about-header {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-bottom: 40px;
      padding-bottom: 20px;
      opacity: 0;
      transform: translateY(20px);
      transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);

      &.fade-in {
        opacity: 1;
        transform: translateY(0);
      }

      .title-box {
        text-align: center;
        padding: 20px 20px;
        border: 1px solid #e5e5e5;
        position: relative;
        background: #fff;
        z-index: 1;
        border-radius: 4px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);

        h1 {
          font-size: 36px;
          color: #333;
          margin-bottom: 16px;
          position: relative;
          display: inline-block;

          &::after {
            content: '';
            position: absolute;
            bottom: -4px;
            left: 50%;
            transform: translateX(-50%);
            width: 60px;
            height: 3px;
            background: #2b5dff;
            border-radius: 1.5px;
          }
        }

        p {
          font-size: 18px;
          color: #666;
          margin: 0;
        }
      }

      .title-border {
        position: absolute;
        top: 10px;
        left: 50%;
        transform: translateX(-50%);
        width: calc(100% - 40px);
        max-width: 400px;
        height: 100%;
        border: 1px solid rgba(43, 93, 255, 0.1);
        border-radius: 4px;
        z-index: 0;
      }
    }

    .filter-bar {
      opacity: 0;
      transform: translateY(20px);
      transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      transition-delay: 0.2s;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 30px;
      padding: 0 0 20px;
      border-bottom: 1px solid #eee;

      &.fade-in {
        opacity: 1;
        transform: translateY(0);
      }

      .sort-options {
        display: flex;
        gap: 20px;

        .sort-item {
          position: relative;
          font-size: 14px;
          color: #666;
          cursor: pointer;
          padding: 6px 12px;
          border-radius: 6px;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 6px;

          &.active {
            color: #2b5dff;
            font-weight: 500;

            &::after {
              content: '';
              position: absolute;
              left: 0;
              right: 0;
              bottom: -21px;
              height: 2px;
              background: #2b5dff;
            }
          }

          &:hover {
            color: #2b5dff;
          }
        }
      }

      .search-box {
        width: 300px;

        :deep(.el-input__wrapper) {
          box-shadow: 0 0 0 1px #e4e7ed inset;
          
          &:hover {
            box-shadow: 0 0 0 1px #2b5dff inset;
          }
          
          &.is-focus {
            box-shadow: 0 0 0 1px #2b5dff inset;
          }
        }

        :deep(.search-icon) {
          color: #999;
          margin-right: 4px;
        }
      }
    }

    .news-grid {
      opacity: 0;
      transform: translateY(20px);
      transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      transition-delay: 0.3s;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 30px;
      margin-bottom: 40px;

      &.fade-in {
        opacity: 1;
        transform: translateY(0);
      }

      .news-item {
        background: #fff;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
        border: 1px solid rgba(0, 0, 0, 0.05);
        opacity: 0;
        transform: translateY(30px);
        transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1) !important;
        will-change: opacity, transform;
        cursor: pointer;

        &.animate {
          opacity: 1;
          transform: translateY(0);
        }

        &:hover {
          transform: translateY(-10px);
          box-shadow: 0 16px 32px rgba(0, 0, 0, 0.08);
          border-color: transparent;

          .news-image {
            img {
              transform: scale(1.05);
            }

            .image-overlay {
              opacity: 1;

              .el-icon {
                transform: scale(1);
              }
            }
          }

          .news-content h3 {
            color: #2b5dff;
          }
        }

        .news-image {
          height: 200px;
          overflow: hidden;
          position: relative;

          &::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 60px;
            background: linear-gradient(to top, rgba(0, 0, 0, 0.3), transparent);
          }

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .image-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.4);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s;

            .el-icon {
              font-size: 32px;
              color: #fff;
              transform: scale(0.8);
              transition: transform 0.3s;
            }
          }
        }

        .news-content {
          padding: 24px;

          h3 {
            font-size: 20px;
            color: #333;
            margin-bottom: 12px;
            font-weight: 600;
            transition: color 0.3s;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            line-height: 1.5;
          }

          .description {
            font-size: 14px;
            color: #666;
            line-height: 1.6;
            margin-bottom: 20px;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            height: 67px;
          }

          .news-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            color: #999;
            font-size: 14px;

            .meta-info {
              display: flex;
              gap: 16px;

              span {
                display: flex;
                align-items: center;
                gap: 4px;
              }
            }

            .views {
              display: flex;
              align-items: center;
              gap: 4px;
            }
          }
        }
      }
    }

    .pagination {
      opacity: 0;
      transform: translateY(20px);
      transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      transition-delay: 0.4s;
      display: flex;
      justify-content: center;
      margin-top: 40px;

      &.fade-in {
        opacity: 1;
        transform: translateY(0);
      }

      :deep(.el-pagination) {
        --el-pagination-button-bg-color: #fff;
        --el-pagination-hover-color: #2b5dff;
        
        .el-pager li {
          &:not(.is-active):hover {
            color: #2b5dff;
          }
          
          &.is-active {
            background-color: #2b5dff;
          }
        }
      }
    }

    .empty-state {
      padding: 60px 0;
      text-align: center;
    }
  }
}

@media (max-width: 992px) {
  .news-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .filter-bar {
    .sort-options {
      .sort-item {
        padding: 4px 8px !important;
        font-size: 13px !important;
      }
    }
  }
}

@media (max-width: 768px) {
  .news-grid {
    grid-template-columns: 1fr !important;
  }

  .filter-bar {
    flex-direction: column;
    gap: 20px;
    padding: 0 0 20px !important;

    .search-box {
      width: 100% !important;
    }

    .sort-options {
      width: 100%;
      justify-content: space-between;

      .sort-item {
        &.active::after {
          bottom: -11px !important;
        }
      }
    }
  }
}
</style> 